import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useOrganization, useUsers } from "../../../components/Common/HelperComponents/GetDataFromStore";
import {
  addEvidenceLink,
  completeEvidenceUpload,
  createAuditComment,
  createEvidenceUploadUrl,
  deleteEvidence,
  getAudit,
  getAuditControl,
  getEvidenceDownloadUrl,
  listAudits,
  listAuditControls,
  transitionAuditControl,
  unwrapGet,
  unwrapPost,
  updateAuditControl,
} from "../../../helpers/auditHub_helper";
import { REVIEW_STATUSES } from "../AuditHub/constants";
import {
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_SUBTLE,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  AuditHubThemeProvider,
  auditHubPageSx,
  cardShellSx,
  outlinedButtonSx,
  primaryButtonSx,
} from "../AuditHub/theme";
import { excludeMspUsers, formatUserLabel } from "../AuditHub/utils";

const norm = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const frameworkMatches = (compliance, framework) => {
  const target = norm(compliance);
  if (!target || !framework) return false;
  const key = String(framework.complianceKey || "");
  const keyBase = key.replace(/_(aws|azure|gcp|oci)$/i, "");
  return [framework.complianceKey, keyBase, framework.name].some((candidate) => norm(candidate) === target);
};

const sameText = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const statusLabel = (value) =>
  REVIEW_STATUSES.find((item) => item.value === value)?.label || value || "—";

const formatWhen = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatBytes = (size) => {
  const n = Number(size);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const initials = (email) => {
  const local = String(email || "").split("@")[0];
  const bits = local.split(/[.\s_-]+/).filter(Boolean);
  if (!bits.length) return "?";
  return bits.slice(0, 2).map((bit) => bit.slice(0, 1).toUpperCase()).join("");
};

async function loadScopeControls(auditId, auditScopeId) {
  const controls = [];
  let pageNo = 1;
  let total = Infinity;
  while (controls.length < total && pageNo <= 20) {
    const roster = unwrapGet(
      await listAuditControls(auditId, {
        auditScopeId,
        page: pageNo,
        size: 100,
      })
    );
    const items = Array.isArray(roster?.items) ? roster.items : [];
    controls.push(...items);
    total = Number(roster?.total);
    if (!Number.isFinite(total) || !items.length) break;
    pageNo += 1;
  }
  return controls;
}

const findScope = (detail, accountNumber, compliance) => {
  const accounts = Array.isArray(detail?.accounts) ? detail.accounts : [];
  for (const account of accounts) {
    if (account.accountNumber !== accountNumber) continue;
    const scope = (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework));
    if (scope) return scope;
  }
  return null;
};

async function loadRoster(accountNumber, compliance) {
  const page = unwrapGet(await listAudits({ status: "active", accountNumber, page: 1, size: 100 }));
  const audits = Array.isArray(page?.items) ? page.items : [];
  const ordered = [...audits].sort((a, b) => String(b.lastActivityAt || "").localeCompare(String(a.lastActivityAt || "")));
  for (const summary of ordered) {
    const detail = unwrapGet(await getAudit(summary.auditId));
    const scope = findScope(detail, accountNumber, compliance);
    if (!scope) continue;
    const controls = await loadScopeControls(detail.auditId, scope.auditScopeId);
    return { audit: detail, controls };
  }
  return { audit: null, controls: [] };
}

async function loadRosterForAudit(auditId, accountNumber, compliance) {
  const detail = unwrapGet(await getAudit(auditId));
  const scope = findScope(detail, accountNumber, compliance);
  if (!scope) return { audit: detail, controls: [] };
  const controls = await loadScopeControls(detail.auditId, scope.auditScopeId);
  return { audit: detail, controls };
}

export function useAuditControlIndex(accountNumber, compliance, auditId) {
  const [state, setState] = useState({ status: "idle", audit: null, controls: [], error: null });

  useEffect(() => {
    if (!accountNumber || !compliance) {
      setState({ status: "idle", audit: null, controls: [], error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", audit: null, controls: [], error: null });
    const load = auditId
      ? loadRosterForAudit(auditId, accountNumber, compliance)
      : loadRoster(accountNumber, compliance);
    load
      .then((result) => {
        if (!cancelled) setState({ status: "ready", ...result, error: null });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            status: "error",
            audit: null,
            controls: [],
            error: error?.message || "Unable to load the audit roster.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountNumber, compliance, auditId]);

  const findControl = (controlId, title) =>
    state.controls.find(
      (item) => sameText(item.controlCode, controlId) || (title && sameText(item.controlTitle, title))
    ) || null;

  return { ...state, findControl };
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem", color: AH_MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: AH_TEXT },
  "& .MuiInputBase-input, & .MuiSelect-select": { fontSize: "0.875rem", color: AH_TEXT },
  "& .MuiSvgIcon-root": { color: AH_MUTED },
};

/** Same blue as the Pair Account launch button. */
const blueButtonSx = {
  ...primaryButtonSx,
  backgroundColor: "#1976d2",
  color: AH_WHITE,
  "&:hover": { backgroundColor: "#1565c0", boxShadow: "none" },
};

const verdictButtonSx = {
  height: 36,
  minHeight: 36,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  boxShadow: "none",
  px: 2,
};

const ControlAuditPanel = ({ roster, controlId, title }) => {
  const { users } = useUsers();
  const organization = useOrganization();
  const match = roster.findControl(controlId, title);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");

  const userOptions = useMemo(
    () =>
      excludeMspUsers(users, organization)
        .filter((user) => user.email)
        .map((user) => ({ email: user.email, label: formatUserLabel(user) })),
    [users, organization]
  );

  const reload = async (auditControlId) => {
    const next = unwrapGet(await getAuditControl(roster.audit.auditId, auditControlId));
    setDetail(next);
  };

  const auditId = roster.audit?.auditId || null;
  const auditControlId = match?.auditControlId || null;

  useEffect(() => {
    if (!auditId || !auditControlId) {
      setDetail(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAuditControl(auditId, auditControlId)
      .then((res) => {
        if (cancelled) return;
        setDetail(unwrapGet(res));
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Unable to load this audit control.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auditId, auditControlId]);

  const run = async (work) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await reload(match.auditControlId);
    } catch (err) {
      setError(err?.message || "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  if (roster.status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading audit workspace…</Typography>
      </Box>
    );
  }

  if (roster.status === "error") {
    return <Typography sx={{ fontSize: 13, color: "#b91c1c", pt: 1 }}>{roster.error}</Typography>;
  }

  if (!roster.audit) {
    return (
      <Typography sx={{ fontSize: 13, color: "#64748b", pt: 1 }}>
        No active audit includes this account and framework.
      </Typography>
    );
  }

  if (!match) {
    return (
      <Typography sx={{ fontSize: 13, color: "#64748b", pt: 1 }}>
        This control is not on the audit roster. The audit API only includes manual controls.
      </Typography>
    );
  }

  const auditCode = roster.audit.auditCode || roster.audit.auditId;
  const allowed = Array.isArray(detail?.allowedTransitions) ? detail.allowedTransitions : [];
  const reviewValue = detail?.reviewStatus || match.reviewStatus;
  const assigneeValue = userOptions.find((user) => user.email === (detail?.assigneeEmail ?? match.assigneeEmail)) || null;
  const evidence = Array.isArray(detail?.evidence) ? detail.evidence : [];
  const comments = Array.isArray(detail?.comments) ? detail.comments : [];
  const canPass = allowed.includes("PASSED");
  const canFail = allowed.includes("FAILED");

  const transition = (toStatus) =>
    run(async () => {
      unwrapPost(await transitionAuditControl(roster.audit.auditId, match.auditControlId, { toStatus }));
    });

  const savePatch = (body) =>
    run(async () => {
      unwrapPost(await updateAuditControl(roster.audit.auditId, match.auditControlId, body));
    });

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await run(async () => {
      const created = unwrapPost(
        await createEvidenceUploadUrl(roster.audit.auditId, match.auditControlId, {
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          sizeBytes: file.size,
        })
      );
      const upload = await fetch(created.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!upload.ok) throw new Error("The file could not be uploaded.");
      unwrapPost(await completeEvidenceUpload(roster.audit.auditId, match.auditControlId, created.evidenceId));
    });
  };

  const onAddLink = () => {
    const url = linkUrl.trim();
    const label = linkLabel.trim();
    if (!url || !label) return;
    setLinkOpen(false);
    setLinkUrl("");
    setLinkLabel("");
    run(async () => {
      unwrapPost(await addEvidenceLink(roster.audit.auditId, match.auditControlId, { linkUrl: url, linkLabel: label }));
    });
  };

  const onDownload = (evidenceId) =>
    run(async () => {
      const result = unwrapGet(await getEvidenceDownloadUrl(roster.audit.auditId, evidenceId));
      if (result?.downloadUrl) window.open(result.downloadUrl, "_blank", "noopener,noreferrer");
    });

  const onPost = () => {
    const body = comment.trim();
    if (!body) return;
    setComment("");
    run(async () => {
      unwrapPost(await createAuditComment(roster.audit.auditId, match.auditControlId, { body }));
    });
  };

  return (
    <AuditHubThemeProvider>
    <Box sx={{ ...cardShellSx, ...auditHubPageSx, mt: 1.5, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 12, color: AH_MUTED, fontWeight: 600 }}>{detail?.controlCode || match.controlCode}</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: AH_TEXT }}>{detail?.controlTitle || match.controlTitle}</Typography>
          {detail?.controlDescription ? (
            <Typography sx={{ fontSize: 13, color: AH_SUBTLE, mt: 0.5 }}>{detail.controlDescription}</Typography>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            disabled={!canPass || busy || loading}
            onClick={() => transition("PASSED")}
            sx={{
              ...verdictButtonSx,
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d", boxShadow: "none" },
            }}
          >
            Pass
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!canFail || busy || loading}
            onClick={() => transition("FAILED")}
            sx={{
              ...verdictButtonSx,
              color: "#dc2626",
              borderColor: "#fecaca",
              "&:hover": { borderColor: "#dc2626", backgroundColor: "#fef2f2", boxShadow: "none" },
            }}
          >
            Fail
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.25, mb: 2 }}>
            <TextField label="Audit ID" value={auditCode} InputProps={{ readOnly: true }} sx={fieldSx} />
            <Autocomplete
              options={userOptions}
              value={assigneeValue}
              disabled={busy}
              onChange={(_e, option) => savePatch({ assigneeEmail: option?.email || null })}
              getOptionLabel={(option) => option.label || option.email || ""}
              isOptionEqualToValue={(a, b) => a.email === b.email}
              renderInput={(params) => <TextField {...params} label="Assigned to" sx={fieldSx} />}
            />
            <TextField
              select
              label="Review status"
              value={reviewValue || ""}
              disabled={busy || !allowed.length}
              onChange={(event) => transition(event.target.value)}
              sx={fieldSx}
            >
              {reviewValue && !allowed.includes(reviewValue) ? (
                <MenuItem value={reviewValue}>{statusLabel(reviewValue)}</MenuItem>
              ) : null}
              {allowed.map((value) => (
                <MenuItem key={value} value={value}>{statusLabel(value)}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Due date"
              type="date"
              value={(detail?.dueDate || match.dueDate || "").slice(0, 10)}
              disabled={busy}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => savePatch({ dueDate: event.target.value || null })}
              sx={fieldSx}
            />
          </Box>

          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", color: AH_MUTED, mb: 1 }}>
            EVIDENCE & ARTIFACTS
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
            {evidence.map((item) => {
              const isLink = item.kind === "LINK" || Boolean(item.linkUrl);
              return (
                <Box key={item.evidenceId} sx={{ display: "flex", alignItems: "center", gap: 1.25, border: `1px solid ${AH_BORDER}`, borderRadius: "8px", px: 1.25, py: 1, backgroundColor: AH_WHITE }}>
                  {isLink ? <LinkOutlinedIcon sx={{ color: AH_MUTED }} /> : <InsertDriveFileOutlinedIcon sx={{ color: AH_MUTED }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }} noWrap>
                      {isLink ? item.linkLabel || item.linkUrl : item.fileName || "File"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: AH_MUTED }} noWrap>
                      {isLink
                        ? item.linkUrl
                        : [formatBytes(item.sizeBytes), item.uploadedBy, formatWhen(item.uploadedAt), item.scanStatus].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                  {isLink ? null : (
                    <IconButton size="small" aria-label="Download evidence" disabled={busy} onClick={() => onDownload(item.evidenceId)}>
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    aria-label="Remove evidence"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        unwrapPost(await deleteEvidence(roster.audit.auditId, item.evidenceId));
                      })
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
            {!evidence.length ? <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No evidence yet.</Typography> : null}
          </Box>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button component="label" disabled={busy} sx={primaryButtonSx}>
              Upload file
              <input hidden type="file" onChange={onUpload} />
            </Button>
            <Button disabled={busy} onClick={() => setLinkOpen(true)} sx={blueButtonSx}>
              Add link
            </Button>
          </Box>

          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", color: AH_MUTED, mb: 1 }}>
            ACTIVITY & COMMENTS
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 1.5 }}>
            {comments.map((item) => (
              <Box key={item.commentId} sx={{ display: "flex", gap: 1.25 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: AH_TRACK, color: AH_TEXT, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {initials(item.authorEmail)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }}>
                    {item.authorEmail}
                    <Box component="span" sx={{ fontWeight: 400, color: AH_MUTED, ml: 1 }}>{formatWhen(item.createdAt)}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: AH_TEXT, whiteSpace: "pre-wrap" }}>{item.body}</Typography>
                </Box>
              </Box>
            ))}
            {!comments.length ? <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No comments yet.</Typography> : null}
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add a comment"
              value={comment}
              disabled={busy}
              onChange={(event) => setComment(event.target.value)}
              inputProps={{ maxLength: 4000 }}
              sx={fieldSx}
            />
            <Button variant="contained" disabled={busy || !comment.trim()} onClick={onPost} sx={primaryButtonSx}>
              Post
            </Button>
          </Box>
        </>
      )}

      {error ? <Typography sx={{ fontSize: 13, color: "#b91c1c", mt: 1.5 }}>{error}</Typography> : null}

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, color: AH_TEXT }}>Add link</DialogTitle>
        <DialogContent sx={{ ...auditHubPageSx, display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField label="Label" value={linkLabel} onChange={(event) => setLinkLabel(event.target.value)} inputProps={{ maxLength: 200 }} size="small" sx={fieldSx} />
          <TextField label="URL" value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} inputProps={{ maxLength: 2000 }} size="small" sx={fieldSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLinkOpen(false)} sx={outlinedButtonSx}>Cancel</Button>
          <Button onClick={onAddLink} disabled={!linkUrl.trim() || !linkLabel.trim()} sx={primaryButtonSx}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AuditHubThemeProvider>
  );
};

export default ControlAuditPanel;
